import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { CustomerService } from './customer.service';
import { LocalServiceService } from './local-service.service';
import { CommonService } from './common.service';
type SeoConfig = {
  main_title: string;
  title: string;
  description: string;
  image: string;
  slug: string;
  twitter_user: string;
  twitter_card: string;
  fb_user: string;
  fb_type: string;
  canonical?: string;
};

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  constructor(
    private meta: Meta,
    private title: Title,
    private router: Router,
    private customerService: CustomerService,
    private local: LocalServiceService,
    private commonService: CommonService,
    @Inject(DOCUMENT) private dom: any,
    @Inject("WINDOW") private window: any
  ) { }

  update(config: {
    main_title?: string;
    title?: string;
    description?: string;
    keywords?: string;
    image?: string;
    slug?: string;
    twitter_user?: string;
    twitter_card?: string;
    fb_user?: string;
    fb_type?: string;
    canonical?: string;
    classification?: string;
    subject?: string;
    page_type?: string;
    page_topic?: string;
    abstract?: string;
    resource_type?: string;
    city?: string;
    country?: string;
  }): void {
    // default values
    config = {
      main_title: '',//' - KACHIING',
      title: 'BEST SERVICES SG',
      description: 'Explore the best home based services, from business support to repair solutions nearby. Connect with local neighbourhood experts for device repairs, home services, cleaning and more. Empowering communities through Kachiing.',
      image: '',
      slug: '',
      twitter_user: '',
      twitter_card: 'summary',
      fb_type: 'article',
      fb_user: '',
      classification: 'local services',
      subject: 'home-based services and repairs',
      page_type: 'service information',
      page_topic: 'local business support and repair solutions',
      abstract: 'Find trusted home-based services, device repairs, business support, cleaning, and more. Connect with experts in your neighborhood through Kachiing.',
      resource_type: 'community services',
      city: 'Singapore',
      country: 'SG, Singapore',
      ...config,
    };

    this.title.setTitle(config.title + config.main_title);

    // For google
    this.updateTag({ name: 'description', content: config.description });
    this.updateTag({ name: 'keywords', content: config.keywords });

    // Additional meta tags with dynamic values
    this.updateTag({ name: 'classification', content: config.classification });
    this.updateTag({ name: 'subject', content: config.subject });
    this.updateTag({ name: 'page-type', content: config.page_type });
    this.updateTag({ name: 'page-topic', content: config.page_topic });
    this.updateTag({ name: 'abstract', content: config.abstract });
    this.updateTag({ name: 'resource-type', content: config.resource_type });
    this.updateTag({ name: 'city', content: config.city });
    this.updateTag({ name: 'country', content: config.country });

    // For twitter
    this.updateTag({ name: 'twitter:card', content: config.twitter_card });
    this.updateTag({ name: 'twitter:site', content: config.twitter_user });
    this.updateTag({ name: 'twitter:title', content: config.title });
    this.updateTag({
      name: 'twitter:description',
      content: config.description,
    });
    this.updateTag({ name: 'twitter:image', content: config.image });

    // For Facebook
    this.updateTag({ property: 'og:title', content: config.title });
    this.updateTag({ property: 'og:description', content: config.description });
    this.updateTag({ property: 'og:image', content: config.image });
    this.updateTag({
      property: 'og:image:alt',
      content: config['og:image:alt'] || null,
    });
    this.updateTag({
      property: 'og:image:height',
      content: config['og:image:height'] || null,
    });
    this.updateTag({
      property: 'og:image:width',
      content: config['og:image:width'] || null,
    });
    this.updateTag({ property: 'og:image:secure_url', content: config.image });
    // this.updateTag({
    //   property: 'og:url',
    //   content: `//${this.router.url}`,
    // });

    this.updateTag({
      property: 'og:image:type',
      content: config['og:image:type'] || null,
    });
    this.updateTag({
      property: 'og:locale',
      content: config['og:locale'] || null,
    });
    this.updateTag({ property: 'og:site_name', content: config.fb_user });
    this.updateTag({ property: 'og:type', content: config['og:type'] || null });
    this.createCanonicalURL();
    this.updateNoIndex()

    if (config.canonical) {
      // this.createCanonicalURL(config.canonical)
    }
  }

  updateTag(obj) {
    if (obj && obj.content) {
      //console.log(obj)
      this.meta.updateTag(obj);
    }
  }

  resetMeta(): void {
    this.update({
      main_title: ' - KACHIING',
      title: 'BEST SERVICES SG',
      description: 'Explore the best home based services, from business support to repair solutions nearby. Connect with local neighbourhood experts for device repairs, home services, cleaning and more. Empowering communities through Kachiing.',
      image: '',
      slug: '',
      twitter_user: '',
      twitter_card: 'summary',
      fb_type: 'article',
      fb_user: '',
    } satisfies SeoConfig);
  }

  updateNoIndex(active: boolean = false): void {
    const excludedRoute = [
      'fusionapp.store',
      'sgbojio.com',
      'brands4good.asia'
    ]

    if (active || excludedRoute.includes(this.window?.location?.hostname))
      this.meta.addTag({ name: 'robots', content: 'noindex, nofollow' })
    else
      this.meta.removeTag('name="robots"')
  }

  createCanonicalURL() {
    const existingLink = this.dom.head.querySelector('link[rel="canonical"]');
    if (existingLink) {
      existingLink.setAttribute('href', this.dom.location.href);
    } else {
      let link: HTMLLinkElement = this.dom.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', this.dom.location.href);
      this.dom.head.appendChild(link);
    }
    this.updateTag({
      property: 'og:url',
      content: this.dom.location.href,
    });
  }

  createHreflang() {
    let link: HTMLLinkElement = this.dom.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('href', `url/${this.dom.location.pathname}`);
    this.dom.head.appendChild(link);
  }


  private imgUrl: string = environment.ImageUrl;
  initSchema(data: any): void {
    this.updateSchema('custom', data?.schema)
  }

  addMetaTags(data: any): void {
    if (data && typeof DOMParser !== 'undefined') {

      const metaTags = data.split('\n'); // Assuming data is a string with each meta tag on a new line
      metaTags.forEach(tag => {
        const metaTag = this.dom.createElement('meta');
        const parser = new DOMParser();
        const doc = parser.parseFromString(tag, 'text/html');
        const metaElement = doc.querySelector('meta');
        if (metaElement) {

          const existingProperty = this.dom.head.querySelector(`meta[property='${metaElement.getAttribute('property')}']`);
          const existingName = this.dom.head.querySelector(`meta[name='${metaElement.getAttribute('name')}']`);

          if (existingProperty) {
            this.dom.head.removeChild(existingProperty);
          }
          if (existingName) {
            this.dom.head.removeChild(existingName);
          }

          if (metaElement.getAttribute('property')) {
            metaTag.setAttribute('property', metaElement.getAttribute('property'));
          }
          if (metaElement.getAttribute('name')) {
            metaTag.setAttribute('name', metaElement.getAttribute('name'));
          }
          metaTag.setAttribute('content', metaElement.getAttribute('content'));
          this.dom.head.appendChild(metaTag);
        }
      });
    }
  }

  updateSchema(type: string, schema?: any) {
    if (type != 'custom') {
      this.customerService.getSchema(type)
        .subscribe((resp: any) => {
          const schemas = resp.body
          if (schemas.length > 0) {
            schemas.forEach((schema: any) => {
              try {
                const JSONData = JSON.parse(schema.schema)
                if (schema.category == 'schema') {
                  let script = this.dom.createElement('script');
                  script.type = 'application/ld+json';
                  if (type == 'organization') {
                    script.id = 'organization-schema';
                  }
                  script.text = schema.schema;

                  this.dom.body.appendChild(script);
                } else {
                  Object.keys(JSONData)
                    .map((row) => {
                      const meta = `<meta name="${row}" content="${JSONData[row]}">`
                      this.addMetaTags(meta)
                    })
                }
              } catch (e) {
                // console.log("Something went wrong Schema")
              }
            })
          }
        })
    } else {
      try {
        JSON.parse(schema)
        const schemaJson = schema;

        // Check if the exact same schema already exists in the DOM
        const existingScripts = this.dom.querySelectorAll('script[type="application/ld+json"]');
        let alreadyExists = false;

        existingScripts.forEach(script => {
          try {
            const content = script.textContent?.trim();
            if (content && JSON.stringify(JSON.parse(content)) === schemaJson) {
              alreadyExists = true;
            }
          } catch (e) {
            // ignore invalid JSON
          }
        });

        if (!alreadyExists) {
          const script = this.dom.createElement('script');
          script.type = 'application/ld+json';
          script.text = schema;
          this.dom.body.appendChild(script);
        }
      } catch (e) {
        // console.log("Something went wrong Schema", e)
      }
    }
  }

  removeSchema(forceAll: boolean = false) {
    const existingScripts = this.dom.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => {
      if (!script.getAttribute('id') || script.getAttribute('id') !== 'organization-schema' || forceAll) {
        this.dom.body.removeChild(script);
      }
    });
  }

  initCategorySchema(category?: any, specificCategory?: boolean) {
    let subCategoryId = []
    if (specificCategory) {
      subCategoryId = category
    } else {
      subCategoryId = category?.subCategory?.map((row: any) => row.categorieChildId) || [];
      subCategoryId.push(category?.id);
    }

    this.customerService.getTopCategoryProducts({
      categoryId: subCategoryId
    }).subscribe((response) => {
      const products = response?.body
      const schema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": category?.name + " Services on Kachiing",
        "url": "https://kachiing.com/categories/" + category?.slug,
        "description": category?.description,
        "inLanguage": "en",
        "isPartOf": {
          "@type": "WebSite",
          "@id": "https://kachiing.com/#website"
        },
        "mainEntity": {
          "@type": "ItemList",
          "name": category?.name + " Services",
          "itemListOrder": "http://schema.org/ItemListOrderAscending",
          "itemListElement": products?.map((row: any) => {
            return {
              "@type": "ListItem",
              "position": 1,
              "item": {
                "@type": "Offer",
                "url": "https://kachiing.com/product/" + row.slug,
                "price": 0,
                "priceCurrency": "SGD",
                "priceSpecification": {
                  "@type": "UnitPriceSpecification",
                  "price": 0
                },
                "itemOffered": {
                  "@type": "Service",
                  "name": row.name,
                  "description": row.description,
                  "provider": {
                    "@type": "Organization",
                    "name": row.name + " Services"
                  }
                }
              }
            }
          })
        }
      }
      this.updateSchema('custom', JSON.stringify(schema))
    })
  }

  initNewsSchema(article: any) {

    let description = article.descriptions.join('. ')
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": article?.title,
      "url": "https://kachiing.com/news/" + article?.slug,
      "description": article?.title,
      "mainEntity": {
        "@type": "NewsArticle",
        "headline": article?.title,
        "image": [
          this.imgUrl + article.image
        ],
        "datePublished": new Date(article.created * 1000).toISOString(),
        "dateModified": new Date(article.updated * 1000).toISOString(),
        "author": {
          "@type": "Person",
          "name": article?.author,
          "url": "https://kachiing.com/news/" + article?.slug
        },
        "publisher": {
          "@type": "Organization",
          "name": "Kachiing Today",
          "logo": {
            "@type": "ImageObject",
            "url": "https://kachiing.com/assets/images/icon.png"
          }
        },
        "description": article?.tableOfContents.join('. '),
        "articleBody": description.length > 200 ? description.substring(0, 200) + '...' : description
      }
    }

    this.updateSchema('custom', JSON.stringify(schema))
  }

  initForumSchema(forum: any) {

    const schema = {
      "@context": "https://schema.org",
      "@type": "DiscussionForumPosting",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://kachiing.com/forum/" + forum.uuid
      },
      "headline": forum.title,
      "articleBody": this.local.removeHtmlTagsAndGetTextContent(forum.message),
      "author": {
        "@type": "Person",
        "name": forum?.user?.userName || 'Anonymous'
      },
      "datePublished": new Date(forum.createdAt * 1000).toISOString(),
      "dateModified": new Date(forum.createdAt * 1000).toISOString(),
      "interactionStatistic": {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/CommentAction",
        "userInteractionCount": forum.repliesCount
      }
    }


    this.updateSchema('custom', JSON.stringify(schema))
  }

  updateKeywords(keywords: string) {

    this.updateTag({ name: 'keywords', content: keywords });
  }

  initProductSchema(product: any) {

    const schema = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": product.name,
      "url": "https://kachiing.com/product/" + product.slug,
      "description": product.description,
      "inLanguage": "en",
      "mainEntity": {
        "@type": "Service",
        "name": product.name,
        "url": this.commonService.correctSite(product.shoutWebsite),
        "image": "https://api.kachiing.com/" + (product.image || 'null'),
        "serviceType": product.categoryName,
        "provider": {
          "@type": "Organization",
          "name": product.companyName && product.companyName !== 'null' ? product.companyName : undefined
        },
        "areaServed": {
          "@type": "Country",
          "name": "Singapore"
        },
        "offers": {
          "@type": "Offer",
          "price": 0,
          "priceCurrency": "SGD",
          "availability": "https://schema.org/InStock"
        }
      }
    });

    this.updateSchema('custom', JSON.stringify(schema))
  }
}
